import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './components/auth/AuthContext';
import { useAuth } from './components/auth/AuthContext';
import AppRoutes from './AppRoutes';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Layout } from './components/Layout';
import './css/custom.css';

const Bootstrapper = ({ onReady }) => {
    const auth = useAuth();

    useEffect(() => {
        let mounted = true;

        // Run bootstrap only once per session. This prevents React StrictMode (dev) double-mount
        // and avoids unnecessary token refreshes across re-renders.
        const bootstrapped = sessionStorage.getItem('authBootstrapped');
        if (bootstrapped === 'true') {
            if (onReady && mounted) onReady();
            return;
        }

        const init = async () => {
            try {
                // mark as bootstrapped immediately to prevent concurrent/duplicate calls
                sessionStorage.setItem('authBootstrapped', 'true');

                if (auth && typeof auth.check4PreviousAuthToken === 'function') {
                    await auth.check4PreviousAuthToken();
                }
            } catch (e) {
                // ignore errors during bootstrap but do not retry in this session
                console.error('Auth bootstrap error', e);
            } finally {
                if (mounted && onReady) onReady();
            }
        };
        init();
        return () => { mounted = false; };
    }, [auth, onReady]);

    return null;
};

export default function App() {
    const [ready, setReady] = useState(false);
    const handleReady = React.useCallback(() => setReady(true), []);

    const renderRoutes = () => {
        const publicRoutes = [];
        const protectedRoutes = [];

        AppRoutes.forEach((route, index) => {
            const { element, isProtected, allowedRoles, ...rest } = route;

            if (isProtected) {
                protectedRoutes.push(
                    <Route
                        key={index}
                        {...rest}
                        element={
                            <ProtectedRoute allowedRoles={allowedRoles}>
                                {element}
                            </ProtectedRoute>
                        }
                    />
                );
            } else {
                publicRoutes.push(
                    <Route key={index} {...rest} element={element} />
                );
            }
        });

        return (
            <>
                {publicRoutes}
                {protectedRoutes}
                <Route path="/unauthorized" element={<h2>Unauthorized Access</h2>} />
                <Route path="*" element={<h2>404 Not Found</h2>} />
            </>
        );
    };

    return (
        <AuthProvider>
            <Bootstrapper onReady={handleReady} />
            <Layout>
                <Routes>
                    {ready ? renderRoutes() : <Route path="*" element={<h2>Loading...</h2>} />}
                </Routes>
            </Layout>
        </AuthProvider>
    );
}
